import React, { useState, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import { Translate } from "orion-components/i18n";

const propTypes = {
	notifications: PropTypes.array.isRequired,
	// The options prop allows you to pass in any Material-ui Dialog prop
	// Found here: https://material-ui.com/api/dialog/#props
	options: PropTypes.shape({
		disableBackdropClick: PropTypes.bool,
		disableEscapeKeyDown: PropTypes.bool,
		fullScreen: PropTypes.bool,
		fullWidth: PropTypes.bool,
		maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
		onBackdropClick: PropTypes.func,
		onClose: PropTypes.func,
		onEnter: PropTypes.func,
		onEntered: PropTypes.func,
		onEntering: PropTypes.func,
		onEscapeKeyDown: PropTypes.func,
		onExit: PropTypes.func,
		onExited: PropTypes.func,
		onExiting: PropTypes.func
	}),
	paperPropStyles: PropTypes.object,
	titlePropStyles: PropTypes.object,
	clearSystemNotifications: PropTypes.func.isRequired,
	dir: PropTypes.string
};

const defaultProps = {
	notifications: [],
	dir: "ltr"
};

const stopPropagation = (e) => {
	e.stopPropagation();
};

const splitTextByNewLine = (text) => {
	return text.split(/\n|\\n/).map((text, index, array) => (
		<Fragment key={index}>
			{text}
			{index !== array.length - 1 && <br />}
		</Fragment>
	));
};

const transformNotificationData = (data) => {
	if (
		typeof data === "string" ||
		(Array.isArray(data) && (typeof data[0] === "string" || typeof data[0] === "number"))
	) {
		let content = "";
		if (Array.isArray(data)) {
			content = data.map((item) => (item === "\n" ? "\\n" : item)).join("");
		} else {
			content = data;
		}

		if (content.includes("\n") || content.includes("\\n")) {
			return splitTextByNewLine(content);
		} else {
			return content;
		}
	} else if (data.length > 0 && (typeof data[0] === "object" || typeof data[0] === "string")) {
		const transformedData = data.flatMap((item, index) => {
			const result = [];
			if (typeof item === "string" && (item.includes("\n") || item.includes("\\n"))) {
				result.push(splitTextByNewLine(item));
			} else {
				result.push(
					item["key"] ? (
						<Translate value={item["key"]} {...(item?.count && { count: item.count })} key={index} />
					) : null,
					item["key"] && data.length - 1 !== index ? <br key={`br-${index}`} /> : null,
					item["text"] ? splitTextByNewLine(item["text"]) : null
				);
			}
			return result;
		});

		return transformedData;
	} else {
		return null;
	}
};

const NotificationsDialog = ({
	notifications,
	options,
	paperPropStyles,
	titlePropStyles,
	clearSystemNotifications,
	dir
}) => {
	const styles = {
		dialog: { backgroundColor: "#2c2d2f" },
		title: { color: "#fff" },
		pageCounter: {
			position: "absolute",
			top: "0",
			...(dir === "rtl" && { left: 0 }),
			...(dir === "ltr" && { right: 0 }),
			margin: "0",
			padding: "16px 24px",
			color: "#fff",
			fontSize: "12px"
		},
		text: {
			color: "#B5B9BE",
			minWidth: "552px",
			minHeight: "130px"
		},
		confirm: { color: "#35b7f3" }
	};

	const dispatch = useDispatch();
	const [open, setOpen] = useState(notifications.length > 0);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentNotification, setCurrentNotification] = useState(notifications[0]);

	useEffect(() => {
		// -- open dialog if new notifications and currently closed
		if (open === false) {
			setOpen(notifications.length > 0);
			setCurrentNotification(notifications[0]);
		}
	}, [notifications]);

	const goToNextPage = (newPage) => {
		// -- move on to the next notification if available, or close the dialog
		const newNotification = notifications[newPage - 1];
		if (newNotification) {
			if (newNotification.ack) {
				// -- skip notification if already acknowledged
				goToNextPage(newPage + 1);
			} else {
				setCurrentNotification(newNotification);
				setCurrentPage(newPage);
			}
		} else {
			// -- close dialog and remove system notifications
			setOpen(false);
			dispatch(clearSystemNotifications());
		}
	};

	const onConfirmClick = () => {
		// -- run action associated with confirmation
		currentNotification.confirm.action();

		goToNextPage(currentPage + 1);
	};

	return currentNotification ? (
		<Dialog
			{...options}
			PaperProps={{
				style: {
					...styles.dialog,
					...(paperPropStyles ? paperPropStyles : {})
				}
			}}
			open={open}
			disableEnforceFocus={true}
			scroll="paper"
			onMouseDown={stopPropagation}
			onTouchStart={stopPropagation}
		>
			{currentNotification.title && (
				<DialogTitle style={{ ...styles.title, ...titlePropStyles }} disableTypography={true}>
					{transformNotificationData(currentNotification.title)}
				</DialogTitle>
			)}
			{notifications.length > 1 && <p style={styles.pageCounter}>{`${currentPage}/${notifications.length}`}</p>}
			<DialogContent>
				{currentNotification.textContent && (
					<DialogContentText variant="body2" style={styles.text}>
						{transformNotificationData(currentNotification.textContent)}
					</DialogContentText>
				)}
			</DialogContent>
			<DialogActions>
				{currentNotification.confirm && (
					<Button
						disabled={currentNotification.confirm.disabled}
						onClick={onConfirmClick}
						onMouseDown={stopPropagation}
						onTouchStart={stopPropagation}
						color="primary"
					>
						{currentNotification.confirm.label}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	) : null;
};

NotificationsDialog.propTypes = propTypes;
NotificationsDialog.defaultProps = defaultProps;

export default NotificationsDialog;
